import React, { useState } from 'react'
import { Alert } from 'react-bootstrap'
import {
  type EntityCustomWorkflowRunSummary,
  type RunRelation,
  useGetCustomWorkflowQuery,
  useGetCustomWorkflowRunQuery,
} from '../../../store/customWorkflow/customWorkflows'
import { WorkflowRunStatusBadge } from '../WorkflowRunStatusBadge'
import { hintsFromSchema, renderWorkflowOutputs } from '../workflowOutputs'
import { Spinner } from '../../Spinner/Spinner'
import { RunContextDetail } from '../runContext/RunContextDetail'

const relationLabel = (relation: RunRelation): string | null => {
  switch (relation.type) {
    case 'viaCcq':
      return 'via Client Query'
    case 'viaMeeting':
      return 'via Meeting'
    default:
      return null
  }
}

const formatRunDate = (createdAt: number | null): string | null => {
  if (createdAt == null) return null
  return new Date(createdAt * 1000).toLocaleString()
}

export interface EntityWorkflowRunCardProps {
  run: EntityCustomWorkflowRunSummary
  accountId: string
}

export const EntityWorkflowRunCard: React.FC<EntityWorkflowRunCardProps> = ({ run, accountId }) => {
  const [expanded, setExpanded] = useState(false)
  const { data: workflow } = useGetCustomWorkflowQuery({ accountId, workflowId: run.workflowId }, { skip: !expanded })
  const { data: detail, isFetching, isError } = useGetCustomWorkflowRunQuery({ accountId, workflowId: run.workflowId, runId: run.id }, { skip: !expanded })

  const relation = relationLabel(run.relation)
  const runDate = formatRunDate(run.createdAt)

  return (
    <div className='border rounded-2 p-3 d-flex flex-column zocks-gap-2'>
      <button
        type='button'
        className='btn btn-link text-reset text-decoration-none p-0 d-flex justify-content-between align-items-start zocks-gap-2 w-100'
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <div className='d-flex flex-column text-start'>
          <span className='fw-semibold'>{run.workflowName || 'Workflow run'}</span>
          {runDate ? <span className='paragraph-small zocks-text-gray-600'>{runDate}</span> : null}
        </div>
        <div className='d-flex align-items-center zocks-gap-2'>
          {relation ? <span className='paragraph-small zocks-text-gray-600'>{relation}</span> : null}
          <WorkflowRunStatusBadge status={run.status} />
          <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden='true' />
        </div>
      </button>
      {expanded ? (
        <div className='d-flex flex-column zocks-gap-2'>
          {run.context?.length ? <RunContextDetail context={run.context} accountId={accountId} /> : null}
          {isFetching ? (
            <div className='d-flex justify-content-center pt-2'>
              <Spinner />
            </div>
          ) : null}
          {isError ? (
            <Alert variant='warning' className='mb-0'>
              Could not load the run output.
            </Alert>
          ) : null}
          {!isFetching && !isError && detail ? (
            detail.error ? (
              <Alert variant='danger' className='mb-0'>
                {detail.error}
              </Alert>
            ) : (
              renderWorkflowOutputs(detail.outputs, hintsFromSchema(workflow?.outputSchema), { accountId, workflowId: run.workflowId, runId: run.id })
            )
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
